import React, { RefObject } from 'react';
import { PauseCircle, PlayCircle, Edit2, Save, Ban, Unlock } from 'lucide-react';
import { Conversation } from '../../types';
import { getDisplayName } from '../../pages/conversations.utils.ts';
import { getAvatarInitial, useClientAvatar } from '../../hooks/useClientAvatar.ts';

export type ConversationsChatHeaderProps = {
  conversation: Conversation;
  isMobile: boolean;
  isEditingName: boolean;
  editedName: string;
  setEditedName: (value: string) => void;
  editContainerRef: RefObject<HTMLDivElement>;
  isBlocked: boolean;
  isBlocking: boolean;
  onPhotoClick: () => void;
  onPhotoKeyDown: (event: React.KeyboardEvent) => void;
  onPhotoChange?: (photo: string | null) => void;
  onNameKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onSaveName: () => void;
  onEditName: () => void;
  onToggleBlocked: () => void;
  onToggleBot: () => void;
};

export default function ConversationsChatHeader({
  conversation,
  isMobile,
  isEditingName,
  editedName,
  setEditedName,
  editContainerRef,
  isBlocked,
  isBlocking,
  onPhotoClick,
  onPhotoKeyDown,
  onPhotoChange,
  onNameKeyPress,
  onSaveName,
  onEditName,
  onToggleBlocked,
  onToggleBot,
}: ConversationsChatHeaderProps) {
  const displayName = getDisplayName(conversation);
  const { photo, handleImageError } = useClientAvatar({
    clienteId: conversation.id,
    photo: conversation.photo,
    onPhotoChange,
  });

  return (
    <div
      className={`flex items-center justify-between px-4 py-3 border-b border-gray-200 ${
        isMobile ? 'sticky top-0 z-10 bg-white shadow-sm' : ''
      }`}
    >
      <div className="flex items-center">
        <div
          className={`h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center text-white select-none transition-transform duration-200 ease-out
            ${photo ? 'cursor-zoom-in hover:scale-110 focus-visible:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-400' : ''}`}
          onClick={photo ? onPhotoClick : undefined}
          onKeyDown={photo ? onPhotoKeyDown : undefined}
          role={photo ? 'button' : undefined}
          tabIndex={photo ? 0 : -1}
          aria-label={photo ? 'Ver foto do cliente' : undefined}
        >
          {photo ? (
            <img
              src={photo}
              alt={displayName || 'Foto'}
              className="w-full h-full object-cover rounded-full"
              onError={handleImageError}
            />
          ) : (
            getAvatarInitial(displayName)
          )}
        </div>
        <div className="ml-3">
          <div className="flex items-center gap-2">
            {isEditingName ? (
              <div ref={editContainerRef} className="flex items-center gap-2">
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={onNameKeyPress}
                  className="text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                />
                <button
                  onClick={onSaveName}
                  className="p-1 rounded transition-colors"
                  style={{
                    marginTop: 0,
                    backgroundColor: 'white',
                    color: '#6b7280'
                  }}
                  title="Salvar"
                >
                  <Save size={14} style={{ color: '#6b7280' }} />
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-900">
                  {displayName}
                </p>
                <button
                  onClick={onEditName}
                  className="p-1 rounded transition-colors"
                  style={{
                    marginTop: 0,
                    backgroundColor: 'white',
                    color: '#6b7280'
                  }}
                  title="Editar nome"
                >
                  <Edit2 size={14} style={{ color: '#6b7280' }} />
                </button>
                <button
                  onClick={onToggleBlocked}
                  className={`p-1 rounded transition-colors disabled:opacity-50 ${isBlocked ? 'text-emerald-600 hover:text-emerald-700' : 'text-gray-400 hover:text-red-600'}`}
                  title={isBlocked ? 'Reativar atendimento automático' : 'Desativar atendimento automático'}
                  disabled={isBlocking}
                >
                  {isBlocked ? <Unlock size={14} /> : <Ban size={14} />}
                </button>
              </>
            )}
          </div>
          <p className="text-xs text-gray-500">{conversation.phoneNumber}</p>
        </div>
      </div>
      <button
        onClick={onToggleBot}
        className={`flex items-center px-3 py-1 rounded text-sm font-medium text-white transition-colors ${
          conversation.botActive
            ? 'bg-red-500 hover:bg-red-600'
            : 'bg-green-500 hover:bg-green-600'
        }`}
      >
        {conversation.botActive ? (
          <>
            <PauseCircle size={16} className="mr-1" />
            Pausar IA nesta conversa
          </>
        ) : (
          <>
            <PlayCircle size={16} className="mr-1" />
            Retomar IA
          </>
        )}
      </button>
    </div>
  );
}
